import { useCallback, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { Controller, useForm } from "react-hook-form";
import { animated, config, useTransition } from "react-spring";

import IconClose from "lib/assets/close-toast.svg";
import IconInfo from "lib/assets/info-toast.svg";
import IconButton from "lib/components/IconButton";
import CommonService from "domain/common/commonService";
import { useEvent } from "lib/contexts/eventContext";
import { useTheme } from "lib/contexts/themeContext";
import { AsyncStatus, UIEvent } from "lib/enums";
import dialCodes from "lib/select-options/dialCodes";
import Select, { SelectOption } from "./Select";
import TextField from "lib/components/TextField";
import Button from "./Button";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import { useTranslations } from "lib/i18n";

const SnackbarDownloadApp = () => {
  if (isMobile) {
    return <DownloadAppMobile />;
  }
  return <DownloadAppDesktop />;
};

export default SnackbarDownloadApp;

/**
 * Country codes.
 */
const COUNTRY_CODE_OPTIONS = dialCodes as SelectOption[];

const DownloadAppDesktop = () => {
  const t = useTranslations("common");
  const { breakpoints } = useTheme();
  const { track } = useEvent();
  const [isVisible, setIsVisible] = useState(false);
  const [isResultVisible, setIsResultVisible] = useState(false);
  const [status, setStatus] = useState(AsyncStatus.Initial);
  const transitions = useTransition(isVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  });
  const resultTransitions = useTransition(isResultVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  });
  const { control, handleSubmit } = useForm({
    defaultValues: {
      mobileCountry: "",
      mobileNumber: "",
    },
  });

  const disable = useCallback(() => {
    sessionStorage.setItem("disableDownloadApp", "true");
  }, []);

  const showResult = useCallback(() => {
    setIsResultVisible(true);
    setTimeout(() => setIsResultVisible(false), 3000);
  }, []);

  const handleOnClose = useCallback(() => {
    setIsVisible(false);
    disable();
  }, [disable]);

  const handleOnSubmit = handleSubmit(
    async ({ mobileCountry, mobileNumber }) => {
      try {
        setStatus(AsyncStatus.Requested);
        await new CommonService().sendAppDownloadLink({
          mobileCountry,
          mobileNumber,
        });
        handleOnClose();
        setTimeout(showResult, 700);
        track(UIEvent.CaseTrackerPhoneNumberSubmitted);
      } catch (e) {
        setStatus(AsyncStatus.Error);
      }
    },
  );

  const handleOnResultClose = useCallback(() => {
    setIsResultVisible(false);
  }, []);

  useEffect(() => {
    if (!isMobile && !sessionStorage.disableDownloadApp) {
      setIsVisible(true);
    }
  }, []);

  // Hide Intercom launcher.
  useEffect(() => {
    const styleId = "hideIntercomLauncher";
    if (isVisible) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = ".intercom-launcher { display: none; }";
      document.head.appendChild(style);
    } else {
      const style = document.getElementById(styleId);
      if (style) {
        style.parentNode.removeChild(style);
      }
    }
  }, [isVisible]);

  return (
    <>
      {transitions(
        (styles, item) =>
          !!item && (
            // eslint-disable-next-line
            // @ts-ignore
            <animated.div
              style={{
                ...styles,
                y: styles.opacity.to([0, 1], [20, 0]),
                position: "fixed",
                zIndex: 999,
                left: 0,
                right: 0,
                bottom: 0,
                padding: "0 20px",
              }}
            >
              <form
                css={`
                  margin: 0 auto 20px;
                  border-radius: 10px;
                  padding: 18px 20px 18px 30px;
                  max-width: 1000px;
                  display: flex;
                  justify-content: space-between;
                  background-color: white;
                  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.16);
                  ${breakpoints.down("sm")} {
                    padding-left: 20px;
                  }
                `}
                onSubmit={handleOnSubmit}
              >
                <div
                  css={`
                    flex: 1;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    ${breakpoints.down("sm")} {
                      flex-direction: column;
                      align-items: flex-start;
                    }
                  `}
                >
                  <div
                    css={`
                      width: 100%;
                      flex: 1;
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                    `}
                  >
                    <div
                      css={`
                        flex: 1;
                        margin-right: 25px;
                        line-height: 24px;
                        font-size: 16px;
                        ${breakpoints.down("sm")} {
                          margin-bottom: 10px;
                        }
                      `}
                    >
                      {t("downloadApp.promptShort")}
                    </div>
                    <button
                      css={`
                        display: none;
                        border: none;
                        padding: 5px 10px;
                        background: none;
                        cursor: pointer;
                        ${breakpoints.down("sm")} {
                          display: block;
                        }
                      `}
                      onClick={handleOnClose}
                    >
                      <IconClose
                        css={`
                          path {
                            fill: #999;
                          }
                        `}
                      />
                    </button>
                  </div>
                  <div
                    css={`
                      display: flex;
                      ${breakpoints.down("xs")} {
                        flex-direction: column;
                      }
                    `}
                  >
                    <div
                      css={`
                        /* flex: 1; */
                        display: flex;
                      `}
                    >
                      <div
                        css={`
                          margin-right: 9px;
                          width: 105px;
                        `}
                      >
                        <Controller
                          control={control}
                          rules={{ required: true }}
                          name="mobileCountry"
                          render={(props) => {
                            return (
                              <Select
                                {...props}
                                css={`
                                  &&& .input-base {
                                    padding-right: 0;
                                  }
                                `}
                                options={COUNTRY_CODE_OPTIONS}
                                placeholder="+"
                              />
                            );
                          }}
                        />
                      </div>
                      <div
                        css={`
                          margin-right: 9px;
                          width: 170px;
                          ${breakpoints.down("sm")} {
                            width: auto;
                          }
                          ${breakpoints.down("xs")} {
                            margin-right: 0;
                          }
                        `}
                      >
                        <Controller
                          control={control}
                          rules={{ required: true }}
                          name="mobileNumber"
                          render={(props) => {
                            return (
                              <TextField
                                {...props}
                                type="tel"
                                placeholder={t("downloadApp.numberPlaceholder")}
                              />
                            );
                          }}
                        />
                      </div>
                    </div>
                    <Button
                      css={`
                        &&& {
                          margin-right: 18px;
                          width: 92px;
                          height: 48px;
                          line-height: 24px;
                          font-size: 16px;
                          font-weight: 500;
                          ${breakpoints.down("xs")} {
                            margin-top: 10px;
                            width: 100%;
                          }
                        }
                      `}
                      type="submit"
                      disabled={status === AsyncStatus.Requested}
                    >
                      {t("downloadApp.send")}
                    </Button>
                  </div>
                </div>
                <div
                  css={`
                    display: flex;
                    ${breakpoints.down("sm")} {
                      display: none;
                    }
                  `}
                >
                  <button
                    css={`
                      border: none;
                      padding: 5px 10px;
                      background: none;
                      cursor: pointer;
                    `}
                    onClick={handleOnClose}
                  >
                    <IconClose
                      css={`
                        path {
                          fill: #999;
                        }
                      `}
                    />
                  </button>
                </div>
              </form>
            </animated.div>
          ),
      )}

      {/* Success */}
      {resultTransitions(
        (styles, item) =>
          !!item && (
            // eslint-disable-next-line
            // @ts-ignore
            <animated.div
              style={{
                ...styles,
                y: styles.opacity.to([0, 1], [20, 0]),
                position: "fixed",
                zIndex: 9999999999, // Cover Intercom launcher underneath the modal.
                left: 0,
                right: 0,
                bottom: 0,
                padding: "0 20px",
              }}
            >
              <div
                css={`
                  margin: 0 auto 20px;
                  border-radius: 10px;
                  padding: 20px 30px 20px 20px;
                  max-width: 1000px;
                  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.16);
                  display: flex;
                  align-items: flex-start;
                  line-height: 24px;
                  font-size: 16px;
                  background-color: white;
                `}
              >
                <div
                  css={`
                    flex: 1;
                    display: flex;
                    align-items: flex-start;
                  `}
                >
                  <IconInfo />
                  <div
                    css={`
                      margin-left: 10px;
                      flex: 1;
                    `}
                  >
                    We have just sent a download link to Lawfully app to your
                    phone! Check your text message.
                  </div>
                </div>
                <div
                  css={`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <button
                    css={`
                      display: flex;
                      border: none;
                      background: none;
                      cursor: pointer;
                    `}
                    onClick={handleOnResultClose}
                  >
                    <IconClose
                      css={`
                        path {
                          fill: #999;
                        }
                      `}
                    />
                  </button>
                </div>
              </div>
            </animated.div>
          ),
      )}
    </>
  );
};

const DownloadAppMobile = () => {
  const t = useTranslations("common");
  const { track } = useEvent();
  const [isVisible, setIsVisible] = useState(false);
  const transitions = useTransition(isVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  });

  const handleOnClose = useCallback(() => {
    setIsVisible(false);
    sessionStorage.setItem("disableDownloadApp", "true");
  }, []);

  const handleOnClick = useCallback(() => {
    setIsVisible(false);
    track(UIEvent.CaseTrackerMobileAppDownload);
  }, [track]);

  useEffect(() => {
    if (isMobile && !sessionStorage.disableDownloadApp) {
      setTimeout(() => {
        setIsVisible(true);
      }, 1000);
    }
  }, []);

  return transitions(
    (styles, item) =>
      !!item && (
        // eslint-disable-next-line
        // @ts-ignore
        <animated.div
          style={{
            ...styles,
            y: styles.opacity.to([0, 1], [10, 0]),
            position: "fixed",
            zIndex: 9999999999, // Cover Intercom launcher underneath the modal.
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div
            css={`
              margin: 10px;
              border-radius: 10px;
              padding: 0px;
              line-height: 22px;
              font-size: 14px;
              background-color: white;
              box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.16);
            `}
          >
            <div
              css={`
                display: flex;
                align-items: flex-start;
              `}
            >
              <div
                css={`
                  flex: 1;
                  display: flex;
                  align-items: center;
                  padding: 20px 0 20px 20px;
                `}
              >
                <div
                  css={`
                    flex: 1;
                    padding-right: 20px;
                  `}
                >
                  {t("downloadApp.tagline")}
                </div>
                <div>
                  <a
                    css={`
                      display: block;
                      border-radius: 30px;
                      padding: 12px 20px;
                      text-align: center;
                      line-height: 24px;
                      font-size: 16px;
                      background-color: ${({ theme }) =>
                        theme.palette.primary.main};
                      color: white;
                      :hover {
                        color: white;
                      }
                    `}
                    href={getAirbridgeTrackerLink(
                      true,
                      "Case Tracker",
                      "Snackbar",
                    )}
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleOnClick}
                  >
                    {t("downloadApp.install")}
                  </a>
                </div>
              </div>
              <IconButton onClick={handleOnClose}>
                <IconClose
                  css={`
                    path {
                      fill: #999;
                    }
                  `}
                />
              </IconButton>
            </div>
          </div>
        </animated.div>
      ),
  );
};
